/**
 * EPIC-063 / 63.2 — Champ « Code parrainage (optionnel) » des pages
 * d'activation (`demarrage-existante.html` / `demarrage-creation.html`).
 *
 * Responsabilités :
 *  - Pré-remplir le champ depuis `sessionStorage['notamicro_referral_code']`
 *    (posé par le script vanilla des layouts `auth.html` / `app.html` ou
 *    par la vitrine via propagation `?r=` aux liens app).
 *  - À chaque saisie (debounce 400 ms), appeler
 *    `GET /me/referral-code/resolve?code=…` et afficher un retour
 *    visuel discret en zone `aria-live=polite` :
 *      · 200 → « Code valide — vous serez rattaché à [parrainOrgName] »
 *      · 404 → « Code parrainage inconnu »
 *      · vide ou < 6 caractères → message effacé
 *  - Aucun blocage du formulaire — le champ reste optionnel. Le code
 *    est transmis tel quel au `POST /billing/checkout-intent`, qui
 *    le persiste en attente sur `organizations.referral_code_pending`
 *    (silencieux si invalide).
 *
 * Privacy by design : le serveur renvoie 404 indistinctement pour
 * « code inconnu », « org inactive » et « auto-parrainage ». Le UI
 * en hérite — pas de message qui distinguerait les trois cas.
 */

import BaseController from "./base_controller.js";
import { api } from "../services/api.js";

const STORAGE_KEY = "notamicro_referral_code";
const DEBOUNCE_MS = 400;
const CODE_LENGTH = 6;
// Alphabet aligné sur `ReferralCodeService::ALPHABET` (A-Z sans I/O, 2-9).
const CODE_REGEX = /^[A-HJ-NP-Z2-9]{6}$/;

export default class ReferralCodeFieldController extends BaseController {
  static targets = ["input", "feedback"];

  connect() {
    this.debounceTimer = null;

    if (this.hasInputTarget) {
      const stored = this.readStored();
      if (stored !== null && this.inputTarget.value === "") {
        this.inputTarget.value = stored;
        this.resolve();
      }
    }
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  onInput() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => this.resolve(), DEBOUNCE_MS);
  }

  async resolve() {
    if (!this.hasInputTarget) return;

    const raw = this.inputTarget.value.trim().toUpperCase();
    if (raw !== this.inputTarget.value) {
      this.inputTarget.value = raw;
    }

    if (raw === "") {
      this.clearFeedback();
      return;
    }

    if (!CODE_REGEX.test(raw)) {
      // Format invalide en local — on n'appelle pas le serveur, mais on
      // n'affiche pas non plus d'erreur tant que le code n'est pas complet
      // (UX : laisser l'utilisateur finir sa saisie).
      if (raw.length >= CODE_LENGTH) {
        this.setFeedback("error", "Code parrainage inconnu.");
      } else {
        this.clearFeedback();
      }
      return;
    }

    try {
      const data = await api.get("/me/referral-code/resolve", { code: raw });
      const name = data?.parrainOrgName ?? "votre parrain";
      this.setFeedback("ok", `Code valide — vous serez rattaché à ${name}.`);
    } catch (err) {
      if (err && err.status === 404) {
        this.setFeedback("error", "Code parrainage inconnu.");
        return;
      }
      // Autres erreurs (réseau, 5xx) — on n'invalide pas le champ
      // optionnel. Le checkout-intent acceptera la valeur ; si elle
      // est réellement invalide, le webhook l'ignorera silencieusement.
      this.clearFeedback();
    }
  }

  setFeedback(kind, message) {
    if (!this.hasFeedbackTarget) return;
    this.feedbackTarget.textContent = message;
    this.feedbackTarget.dataset.state = kind;
    this.feedbackTarget.hidden = false;
  }

  clearFeedback() {
    if (!this.hasFeedbackTarget) return;
    this.feedbackTarget.textContent = "";
    delete this.feedbackTarget.dataset.state;
    this.feedbackTarget.hidden = true;
  }

  readStored() {
    try {
      const value = sessionStorage.getItem(STORAGE_KEY);
      return value && CODE_REGEX.test(value) ? value : null;
    } catch (_e) {
      return null;
    }
  }
}
